/**
 * H2 / H2-F — Creator: the ONE utilities panel for a Kickback. Lists every attached utility
 * (Marketplace, Vault, Creator-created) from the shared association source, with unlock rules,
 * launch readiness, and attachment actions. Backend enforces all protections.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { SlidersHorizontal, Lock, AlertTriangle } from 'lucide-react';
import theme from '../theme';
import UtilityRuleEditor from './UtilityRuleEditor';
import CreatorRedemptionsPanel from './CreatorRedemptionsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOURCE_LABEL = { marketplace: 'Utility Marketplace', vault: 'Utility Vault', kickback: 'Created with this Kickback', wizard: 'Created in the Kickback wizard' };
const small = { padding: '6px 10px', borderRadius: 8, border: `1px solid ${theme.colors.borders}`, background: '#fff', fontSize: 12, cursor: 'pointer' };

function RowActions({ u, portfolioId, auth, onDone }) {
  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); onDone(); }
    catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === 'string' ? d : d?.errors?.join(' ') || 'Action failed'); }
  };
  const tid = u.association_key.replace(/[^a-zA-Z0-9-]/g, '-');
  if (u.source === 'vault') {
    const patch = (action) => act(() => axios.patch(`${API}/creator/portfolios/${portfolioId}/utilities/attached/${u.source_id}`, { action }, auth), { deactivate: 'Utility deactivated', detach: 'Utility detached', activate: 'Utility reactivated' }[action]);
    return u.attachment_status === 'active'
      ? <><button data-testid={`utility-deactivate-${tid}`} style={small} onClick={() => patch('deactivate')}>Deactivate</button>
          <button data-testid={`utility-detach-${tid}`} style={small} onClick={() => patch('detach')}>Detach</button></>
      : u.attachment_status === 'inactive'
        ? <button data-testid={`utility-reactivate-${tid}`} style={small} onClick={() => patch('activate')}>Reactivate</button> : null;
  }
  if (u.source === 'marketplace') {
    return <button data-testid={`utility-remove-${tid}`} style={small}
      onClick={() => act(() => axios.delete(`${API}/creator/portfolios/${portfolioId}/utilities/${u.source_id}`, auth), 'Utility removed')}>Remove</button>;
  }
  return null;
}

export default function UtilityRulesPanel({ portfolioId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null);
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/creator/portfolios/${portfolioId}/utility-rules`, auth)).data); }
    catch (e) { setErr(e?.response?.data?.detail || 'Could not load utilities'); }
  }, [portfolioId, auth]);
  useEffect(() => { load(); }, [load]);

  const gaps = Object.fromEntries((data?.launch_check?.blockers || []).map(b => [b.association_key, b.missing]));
  const active = (data?.utilities || []).filter(u => u.attachment_status === 'active');
  return (
    <div data-testid="utility-rules-panel" style={{ background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card, padding: 20, marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <SlidersHorizontal size={20} color={theme.colors.primary} />
        <div style={{ fontWeight: 800, fontSize: 17, flex: 1 }}>Utilities & Unlock Rules <span data-testid="utility-active-count" style={{ fontSize: 13, color: theme.colors.text.muted }}>({active.length} attached)</span></div>
        <button data-testid="utility-attach-marketplace" style={small} onClick={() => navigate('/creator/utilities-marketplace')}>Attach from Marketplace</button>
        <button data-testid="utility-attach-vault" style={small} onClick={() => navigate('/creator/utility-vault')}>Attach from Vault</button>
      </div>
      <div style={{ fontSize: 12, color: theme.colors.text.muted, margin: '4px 0 12px' }}>
        Offering utilities is optional — zero is fine. Any utility you attach needs unlock rules, who fulfils it, and how Holders use it before a new launch. Nothing unlocks for Holders until its rules are saved.
      </div>
      {data?.published && (
        <div data-testid="utility-rules-published-note" style={{ display: 'flex', gap: 8, padding: 10, borderRadius: 8, background: '#FFFBEB', color: '#92400E', fontSize: 12, marginBottom: 10 }}>
          <Lock size={14} /> This Kickback is published or already has Holders ({data.portfolio_status}). You can add bonuses, fill in missing details, and make benefits easier to get — not remove or restrict what Holders were promised.
        </div>
      )}
      {data && !data.launch_check.ok && (
        <div data-testid="utility-launch-gaps" style={{ display: 'flex', gap: 8, padding: 10, borderRadius: 8, background: '#FEF2F2', color: '#991B1B', fontSize: 12, marginBottom: 10 }}>
          <AlertTriangle size={14} />
          <span>{data.published
            ? 'Legacy record: this Kickback went live before utility checks existed. It stays live and existing claims are untouched, but the utilities marked below show to Holders as not set up until you finish them.'
            : 'Launch is blocked until the utilities marked below are finished (or detached).'}</span>
        </div>
      )}
      {(data?.launch_check?.warnings || []).map(w => (
        <div key={w.association_key} data-testid="utility-launch-warning" style={{ fontSize: 12, color: '#92400E', marginBottom: 6 }}>⚠ {w.title}: {w.message}</div>
      ))}
      {err && <div data-testid="utility-rules-error" style={{ color: theme.colors.status.error, fontSize: 13 }}>{err}</div>}
      {data && data.utilities.length === 0 && (
        <div data-testid="utility-rules-empty" style={{ fontSize: 13, color: theme.colors.text.muted }}>No utilities attached. That's allowed — attach one from the Marketplace or Vault if you want to offer benefits.</div>
      )}
      <div style={{ display: 'grid', gap: 10 }}>
        {(data?.utilities || []).map(u => {
          const tid = u.association_key.replace(/[^a-zA-Z0-9-]/g, '-');
          const g = gaps[u.association_key];
          return (
            <div key={u.association_key} data-testid={`utility-rule-row-${tid}`} style={{ padding: 12, background: theme.colors.bg.secondary, borderRadius: 8, opacity: u.attachment_status === 'active' ? 1 : 0.7 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontWeight: 700 }}>{u.title}</div>
                  <div style={{ fontSize: 11, color: theme.colors.text.muted }}>{SOURCE_LABEL[u.source]}{u.provider_name ? ` · ${u.provider_name}` : ''} · {u.attachment_status}</div>
                  <div data-testid={`utility-rule-summary-${tid}`} style={{ fontSize: 12, marginTop: 4, color: u.terms ? theme.colors.text.secondary : '#92400E' }}>
                    {u.terms ? `v${u.terms.version} · ${u.terms.rules.match === 'any' ? 'Any of' : 'All of'}: ${u.rule_summary.join('; ')}${u.terms.surprise?.enabled ? ' · Surprise' : ''}` : 'No unlock rules yet — Holders see "Not set up yet".'}
                  </div>
                  {u.attachment_status === 'active' && (
                    <div data-testid={`utility-readiness-${tid}`} style={{ fontSize: 11, marginTop: 4, fontWeight: 700, color: g ? '#991B1B' : '#065F46' }}>
                      {g ? `Needs: ${g.join(', ')}` : 'Ready to promise'}
                    </div>
                  )}
                </div>
                {u.attachment_status === 'active' && (
                  <button data-testid={`utility-rule-edit-${tid}`} onClick={() => setEditing(editing === u.association_key ? null : u.association_key)}
                    style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: theme.colors.primary, color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                    {editing === u.association_key ? 'Close' : u.terms ? 'Edit rules' : 'Set unlock rules'}
                  </button>
                )}
                <RowActions u={u} portfolioId={portfolioId} auth={auth} onDone={load} />
              </div>
              {editing === u.association_key && (
                <UtilityRuleEditor portfolioId={portfolioId} utility={u} published={data.published} onSaved={() => { setEditing(null); load(); }} />
              )}
            </div>
          );
        })}
      </div>
      <CreatorRedemptionsPanel portfolioId={portfolioId} auth={auth} />
    </div>
  );
}
